import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  BarChart3, Users, FileText, BookOpen, DollarSign, ShieldCheck,
  Loader2, CheckCircle, XCircle, Plus, TrendingUp, UserCheck,
  ClipboardList, Eye, Mail, MapPin, Clock, GraduationCap, Building2
} from 'lucide-react';

export default function AdminPanel() {
  const { user, api } = useAuth();
  const [tab, setTab] = useState('overview');
  const [analytics, setAnalytics] = useState({});
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userFilter, setUserFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [appFilter, setAppFilter] = useState('');
  const [courseDialog, setCourseDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState(null);
  const [mentorDetailDialog, setMentorDetailDialog] = useState(null);
  const [newCourse, setNewCourse] = useState({
    title: '', description: '', price: '', category: '', duration: '', instructor: ''
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [aRes, uRes, pRes, cRes, appRes] = await Promise.all([
        api.get('/admin/analytics'),
        api.get('/admin/users'),
        api.get('/admin/projects'),
        api.get('/courses'),
        api.get('/admin/applications'),
      ]);
      setAnalytics(aRes.data);
      setUsers(uRes.data.users || []);
      setProjects(pRes.data.projects || []);
      setCourses(cRes.data.courses || []);
      setApplications(appRes.data.applications || []);
    } catch (err) {
      toast.error('Failed to load admin data');
    } finally { setLoading(false); }
  };

  const verifyMentor = async (userId, verified) => {
    try {
      await api.put(`/admin/users/${userId}/verify`, { verified });
      toast.success(verified ? 'Mentor verified' : 'Mentor unverified');
      fetchAll();
    } catch { toast.error('Failed to update'); }
  };

  const updateProjectStatus = async (projectId, status) => {
    try {
      await api.put(`/admin/projects/${projectId}/status`, { status });
      toast.success(`Project ${status}`);
      fetchAll();
    } catch { toast.error('Failed to update'); }
  };

  const updateAppStatus = async (applicationId, status) => {
    try {
      await api.put(`/applications/${applicationId}/status`, { status });
      toast.success(`Application ${status}`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const createCourse = async () => {
    if (!newCourse.title || !newCourse.price || !newCourse.category) {
      toast.error('Fill required fields'); return;
    }
    try {
      await api.post('/admin/courses', { ...newCourse, price: parseFloat(newCourse.price) });
      toast.success('Course created!');
      setCourseDialog(false);
      setNewCourse({ title: '', description: '', price: '', category: '', duration: '', instructor: '' });
      fetchAll();
    } catch { toast.error('Failed to create course'); }
  };

  const deleteCourse = async (courseId) => {
    try {
      await api.delete(`/admin/courses/${courseId}`);
      toast.success('Course deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const filteredUsers = userFilter && userFilter !== 'all' ? users.filter(u => u.role === userFilter) : users;
  const filteredProjects = projectFilter && projectFilter !== 'all' ? projects.filter(p => p.status === projectFilter) : projects;
  const filteredApps = appFilter && appFilter !== 'all' ? applications.filter(a => a.status === appFilter) : applications;

  const statusBadge = (s) => {
    if (s === 'approved' || s === 'accepted') return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    if (s === 'rejected') return 'bg-red-50 text-red-700 border-red-100';
    return 'bg-amber-50 text-amber-700 border-amber-100';
  };

  const statCards = [
    { label: 'Total Users', value: analytics.total_users || 0, icon: <Users className="h-5 w-5" />, color: 'text-blue-500', bg: 'bg-blue-50' },
    { label: 'Students', value: analytics.students || 0, icon: <GraduationCap className="h-5 w-5" />, color: 'text-[#1a5c5c]', bg: 'bg-[#1a5c5c]/10' },
    { label: 'Mentors', value: analytics.mentors || 0, icon: <UserCheck className="h-5 w-5" />, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Revenue', value: `$${(analytics.total_revenue || 0).toFixed(2)}`, icon: <DollarSign className="h-5 w-5" />, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Projects', value: analytics.total_projects || 0, icon: <FileText className="h-5 w-5" />, color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Pending Projects', value: analytics.pending_projects || 0, icon: <Clock className="h-5 w-5" />, color: 'text-orange-500', bg: 'bg-orange-50' },
    { label: 'Applications', value: analytics.total_applications || 0, icon: <ClipboardList className="h-5 w-5" />, color: 'text-rose-500', bg: 'bg-rose-50' },
    { label: 'Pro Subs', value: analytics.pro_subscriptions || 0, icon: <TrendingUp className="h-5 w-5" />, color: 'text-indigo-500', bg: 'bg-indigo-50' },
  ];

  if (loading) return (
    <div className="page-container min-h-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 text-[#1a5c5c] animate-spin" />
    </div>
  );

  return (
    <div className="page-container min-h-screen bg-slate-50" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Admin Panel</h1>
            <p className="text-sm text-slate-500 mt-1">Platform management & analytics</p>
          </div>
          <Badge className="bg-[#1a5c5c]/10 text-[#1a5c5c] border-0"><ShieldCheck className="h-3.5 w-3.5 mr-1" /> Admin</Badge>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="overview" data-testid="admin-tab-overview">Analytics</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
            <TabsTrigger value="applications" data-testid="admin-tab-applications">
              Applications
              {applications.filter(a => a.status === 'pending').length > 0 && (
                <span className="ml-1.5 h-5 min-w-5 px-1 rounded-full bg-red-500 text-white text-[10px] inline-flex items-center justify-center">
                  {applications.filter(a => a.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="projects" data-testid="admin-tab-projects">Projects</TabsTrigger>
            <TabsTrigger value="courses" data-testid="admin-tab-courses">Courses</TabsTrigger>
          </TabsList>

          {/* ====== ANALYTICS ====== */}
          <TabsContent value="overview">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {statCards.map((s) => (
                <div key={s.label} className="stat-card rounded-xl p-4" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-lg ${s.bg} flex items-center justify-center ${s.color}`}>{s.icon}</div>
                    <div>
                      <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{s.value}</p>
                      <p className="text-xs text-slate-500">{s.label}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-slate-100 p-6">
                <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope' }}>Pending Mentor Verifications</h3>
                {users.filter(u => u.role === 'mentor' && !u.verified).length === 0 ? (
                  <p className="text-sm text-slate-500">No pending verifications.</p>
                ) : (
                  users.filter(u => u.role === 'mentor' && !u.verified).map(u => (
                    <div key={u.user_id} className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">{u.name}</p>
                        <p className="text-xs text-slate-500">{u.email}</p>
                        {u.profile?.institution && <p className="text-xs text-slate-400">{u.profile.institution}</p>}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setMentorDetailDialog(u)} className="h-7 text-xs" data-testid={`view-mentor-${u.user_id}`}>
                          <Eye className="h-3 w-3 mr-1" /> View
                        </Button>
                        <Button size="sm" onClick={() => verifyMentor(u.user_id, true)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" data-testid={`verify-${u.user_id}`}>
                          <CheckCircle className="h-3 w-3 mr-1" /> Verify
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="bg-white rounded-xl border border-slate-100 p-6">
                <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope' }}>Recent Applications</h3>
                {applications.length === 0 ? (
                  <p className="text-sm text-slate-500">No applications yet.</p>
                ) : (
                  applications.slice(0, 5).map(a => (
                    <div key={a.application_id} className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{a.student_name} <span className="text-slate-400 font-normal">applied to</span> {a.project_title}</p>
                        <p className="text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString()}</p>
                      </div>
                      <Badge className={`${statusBadge(a.status)} border text-xs capitalize`}>{a.status}</Badge>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* ====== USERS ====== */}
          <TabsContent value="users">
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope' }}>User Management ({filteredUsers.length})</h3>
                <Select value={userFilter} onValueChange={setUserFilter}>
                  <SelectTrigger className="w-40" data-testid="user-role-filter"><SelectValue placeholder="All Roles" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="student">Students</SelectItem>
                    <SelectItem value="mentor">Mentors</SelectItem>
                    <SelectItem value="admin">Admins</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="users-table">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-2 px-2 text-xs text-slate-500 font-medium">Name</th>
                      <th className="text-left py-2 px-2 text-xs text-slate-500 font-medium">Email</th>
                      <th className="text-left py-2 px-2 text-xs text-slate-500 font-medium">Role</th>
                      <th className="text-left py-2 px-2 text-xs text-slate-500 font-medium">Status</th>
                      <th className="text-left py-2 px-2 text-xs text-slate-500 font-medium">Plan</th>
                      <th className="text-left py-2 px-2 text-xs text-slate-500 font-medium">Joined</th>
                      <th className="text-right py-2 px-2 text-xs text-slate-500 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.user_id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`user-row-${u.user_id}`}>
                        <td className="py-3 px-2">
                          <div>
                            <p className="font-medium text-slate-900">{u.name}</p>
                            {u.profile?.institution && <p className="text-xs text-slate-400">{u.profile.institution}</p>}
                          </div>
                        </td>
                        <td className="py-3 px-2 text-slate-500">{u.email}</td>
                        <td className="py-3 px-2"><Badge variant="outline" className="text-xs capitalize">{u.role}</Badge></td>
                        <td className="py-3 px-2">
                          <Badge className={`text-xs ${u.verified ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'} border`}>
                            {u.verified ? 'Verified' : 'Pending'}
                          </Badge>
                        </td>
                        <td className="py-3 px-2"><Badge className="bg-[#1a5c5c]/10 text-[#1a5c5c] border-0 text-xs capitalize">{u.subscription?.plan || 'free'}</Badge></td>
                        <td className="py-3 px-2 text-xs text-slate-400">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                        <td className="py-3 px-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {u.role === 'mentor' && (
                              <Button size="sm" variant="ghost" onClick={() => setMentorDetailDialog(u)} className="h-7 text-xs text-slate-600" data-testid={`view-user-${u.user_id}`}>
                                <Eye className="h-3 w-3" />
                              </Button>
                            )}
                            {u.role === 'mentor' && !u.verified && (
                              <Button size="sm" onClick={() => verifyMentor(u.user_id, true)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs">Verify</Button>
                            )}
                            {u.role === 'mentor' && u.verified && (
                              <Button size="sm" variant="outline" onClick={() => verifyMentor(u.user_id, false)} className="text-amber-600 border-amber-200 h-7 text-xs">Unverify</Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* ====== APPLICATIONS ====== */}
          <TabsContent value="applications">
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope' }}>
                  All Applications ({filteredApps.length})
                </h3>
                <Select value={appFilter} onValueChange={setAppFilter}>
                  <SelectTrigger className="w-40" data-testid="app-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filteredApps.length === 0 ? (
                <div className="text-center py-12">
                  <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No applications found.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredApps.map((a) => (
                    <div key={a.application_id}
                      className="border border-slate-100 rounded-lg p-4 hover:border-[#1a5c5c]/20 transition-colors"
                      data-testid={`admin-app-${a.application_id}`}>
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                        {/* Left: Student & Project Info */}
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="h-8 w-8 rounded-full bg-[#1a5c5c] flex items-center justify-center text-white text-xs font-semibold shrink-0">
                              {a.student_name?.charAt(0)?.toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-slate-900">{a.student_name}</p>
                              <p className="text-xs text-slate-500">{a.student_email}</p>
                            </div>
                            {a.student_profile?.university && (
                              <Badge variant="outline" className="text-xs ml-auto md:ml-2">
                                <GraduationCap className="h-3 w-3 mr-1" />{a.student_profile.university}
                              </Badge>
                            )}
                            {a.student_subscription?.plan === 'pro' && (
                              <Badge className="bg-[#d4af37]/10 text-[#b8860b] border-0 text-xs">Pro</Badge>
                            )}
                          </div>

                          <div className="bg-slate-50 rounded-lg p-3">
                            <p className="text-xs text-slate-500 mb-1">Applied to project:</p>
                            <p className="text-sm font-medium text-slate-900">{a.project_title}</p>
                            <div className="flex flex-wrap gap-2 mt-1.5">
                              {a.project_details?.specialty && <Badge className="bg-[#1a5c5c]/10 text-[#1a5c5c] border-0 text-xs">{a.project_details.specialty}</Badge>}
                              {a.project_details?.mentor_name && <span className="text-xs text-slate-500">Mentor: {a.project_details.mentor_name}</span>}
                              {a.project_details?.duration && <span className="text-xs text-slate-400 flex items-center gap-0.5"><Clock className="h-3 w-3" />{a.project_details.duration}</span>}
                              {a.project_details?.mode && <span className="text-xs text-slate-400 flex items-center gap-0.5"><MapPin className="h-3 w-3" />{a.project_details.mode}</span>}
                              {a.project_details?.paid && <Badge className="bg-amber-50 text-amber-700 border-0 text-xs">${a.project_details.price}</Badge>}
                            </div>
                          </div>

                          {a.cover_letter && (
                            <div className="text-xs text-slate-600 bg-blue-50/50 rounded p-2 border border-blue-100/50">
                              <span className="font-medium text-slate-700">Cover Letter:</span> {a.cover_letter}
                            </div>
                          )}

                          {/* Student profile details */}
                          {(a.student_profile?.research_interests?.length > 0 || a.student_profile?.skills?.length > 0) && (
                            <div className="flex flex-wrap gap-1.5">
                              {a.student_profile?.research_interests?.map((interest, i) => (
                                <span key={i} className="text-[10px] bg-[#1a5c5c]/5 text-[#1a5c5c] px-1.5 py-0.5 rounded">{interest}</span>
                              ))}
                              {a.student_profile?.skills?.map((skill, i) => (
                                <span key={i} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{skill}</span>
                              ))}
                            </div>
                          )}

                          <p className="text-xs text-slate-400">Applied: {new Date(a.created_at).toLocaleString()}</p>
                        </div>

                        {/* Right: Status & Actions */}
                        <div className="flex flex-row md:flex-col items-center md:items-end gap-2 shrink-0">
                          <Badge className={`${statusBadge(a.status)} border text-xs capitalize`}>{a.status}</Badge>
                          {a.status === 'pending' && (
                            <div className="flex gap-1.5">
                              <Button size="sm" onClick={() => updateAppStatus(a.application_id, 'accepted')}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs" data-testid={`admin-accept-${a.application_id}`}>
                                <CheckCircle className="h-3.5 w-3.5 mr-1" /> Accept
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => updateAppStatus(a.application_id, 'rejected')}
                                className="text-red-600 border-red-200 hover:bg-red-50 h-8 text-xs" data-testid={`admin-reject-${a.application_id}`}>
                                <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ====== PROJECTS ====== */}
          <TabsContent value="projects">
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope' }}>Project Management ({filteredProjects.length})</h3>
                <Select value={projectFilter} onValueChange={setProjectFilter}>
                  <SelectTrigger className="w-40" data-testid="project-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-3">
                {filteredProjects.map((p) => (
                  <div key={p.project_id} className="flex items-start justify-between py-3 border-b border-slate-50 last:border-0" data-testid={`admin-project-${p.project_id}`}>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{p.title}</p>
                      <p className="text-xs text-slate-500">{p.mentor_name} - {p.specialty} - {p.duration}</p>
                      <div className="flex gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">{p.mode}</Badge>
                        {p.paid ? <Badge className="bg-amber-50 text-amber-700 border-amber-100 text-xs">${p.price}</Badge> : <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 text-xs">Free</Badge>}
                        <span className="text-xs text-slate-400">Slots: {p.slots_filled || 0}/{p.slots_available}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className={`text-xs capitalize ${statusBadge(p.status)} border`}>{p.status}</Badge>
                      {p.status === 'pending' && (
                        <>
                          <Button size="sm" onClick={() => updateProjectStatus(p.project_id, 'approved')} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" data-testid={`approve-project-${p.project_id}`}>Approve</Button>
                          <Button size="sm" variant="outline" onClick={() => updateProjectStatus(p.project_id, 'rejected')} className="text-red-600 border-red-200 h-7 text-xs" data-testid={`reject-project-${p.project_id}`}>Reject</Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* ====== COURSES ====== */}
          <TabsContent value="courses">
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope' }}>Course Management ({courses.length})</h3>
                <Button onClick={() => setCourseDialog(true)} className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white" data-testid="add-course-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Course
                </Button>
              </div>
              {courses.length === 0 ? (
                <p className="text-sm text-slate-500 py-4">No courses yet.</p>
              ) : (
                <div className="space-y-3">
                  {courses.map((c) => (
                    <div key={c.course_id} className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0" data-testid={`admin-course-${c.course_id}`}>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{c.title}</p>
                        <p className="text-xs text-slate-500">{c.category} - {c.duration} - ${c.price}</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => deleteCourse(c.course_id)} className="text-red-600 border-red-200 h-7 text-xs" data-testid={`delete-course-${c.course_id}`}>Delete</Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Course Dialog */}
      <Dialog open={courseDialog} onOpenChange={setCourseDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Add New Course</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-sm">Title *</Label><Input value={newCourse.title} onChange={e => setNewCourse({...newCourse, title: e.target.value})} data-testid="course-title-input" /></div>
            <div><Label className="text-sm">Description</Label><Textarea value={newCourse.description} onChange={e => setNewCourse({...newCourse, description: e.target.value})} rows={3} data-testid="course-desc-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Price (USD) *</Label><Input type="number" step="0.01" value={newCourse.price} onChange={e => setNewCourse({...newCourse, price: e.target.value})} data-testid="course-price-input" /></div>
              <div><Label className="text-sm">Category *</Label><Input value={newCourse.category} onChange={e => setNewCourse({...newCourse, category: e.target.value})} data-testid="course-category-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Duration</Label><Input value={newCourse.duration} onChange={e => setNewCourse({...newCourse, duration: e.target.value})} placeholder="e.g. 8 weeks" /></div>
              <div><Label className="text-sm">Instructor</Label><Input value={newCourse.instructor} onChange={e => setNewCourse({...newCourse, instructor: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCourseDialog(false)}>Cancel</Button>
            <Button onClick={createCourse} className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white" data-testid="submit-course-btn">Create Course</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mentor Detail Dialog */}
      <Dialog open={!!mentorDetailDialog} onOpenChange={() => setMentorDetailDialog(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Mentor Profile Details</DialogTitle></DialogHeader>
          {mentorDetailDialog && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-[#1a5c5c] flex items-center justify-center text-white text-lg font-bold">
                  {mentorDetailDialog.name?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <p className="text-lg font-bold text-slate-900">{mentorDetailDialog.name}</p>
                  <p className="text-sm text-slate-500">{mentorDetailDialog.email}</p>
                </div>
                <Badge className={`ml-auto ${mentorDetailDialog.verified ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'} border text-xs`}>
                  {mentorDetailDialog.verified ? 'Verified' : 'Pending'}
                </Badge>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-3 text-sm">
                {mentorDetailDialog.profile?.institution && (
                  <div><p className="text-xs text-slate-500 mb-0.5">Institution</p><p className="font-medium flex items-center gap-1"><Building2 className="h-3.5 w-3.5 text-slate-400" />{mentorDetailDialog.profile.institution}</p></div>
                )}
                {mentorDetailDialog.profile?.department && (
                  <div><p className="text-xs text-slate-500 mb-0.5">Department</p><p className="font-medium">{mentorDetailDialog.profile.department}</p></div>
                )}
                {mentorDetailDialog.profile?.specialization && (
                  <div><p className="text-xs text-slate-500 mb-0.5">Specialization</p><p className="font-medium">{mentorDetailDialog.profile.specialization}</p></div>
                )}
                {mentorDetailDialog.profile?.secondary_specialization && (
                  <div><p className="text-xs text-slate-500 mb-0.5">Secondary Specialization</p><p className="font-medium">{mentorDetailDialog.profile.secondary_specialization}</p></div>
                )}
                {mentorDetailDialog.profile?.experience && (
                  <div><p className="text-xs text-slate-500 mb-0.5">Experience</p><p className="font-medium">{mentorDetailDialog.profile.experience}</p></div>
                )}
                {mentorDetailDialog.profile?.years_of_experience && (
                  <div><p className="text-xs text-slate-500 mb-0.5">Years</p><p className="font-medium">{mentorDetailDialog.profile.years_of_experience} years</p></div>
                )}
                {mentorDetailDialog.profile?.city && (
                  <div><p className="text-xs text-slate-500 mb-0.5">City</p><p className="font-medium">{mentorDetailDialog.profile.city}</p></div>
                )}
                {mentorDetailDialog.profile?.phone && (
                  <div><p className="text-xs text-slate-500 mb-0.5">Phone</p><p className="font-medium">{mentorDetailDialog.profile.phone}</p></div>
                )}
                {mentorDetailDialog.profile?.linkedin_url && (
                  <div><p className="text-xs text-slate-500 mb-0.5">LinkedIn</p><a href={mentorDetailDialog.profile.linkedin_url} target="_blank" rel="noreferrer" className="font-medium text-[#1a5c5c] underline text-xs break-all">{mentorDetailDialog.profile.linkedin_url}</a></div>
                )}
                {mentorDetailDialog.profile?.orcid_id && (
                  <div><p className="text-xs text-slate-500 mb-0.5">ORCID</p><p className="font-medium">{mentorDetailDialog.profile.orcid_id}</p></div>
                )}
              </div>

              {mentorDetailDialog.profile?.bio && (
                <div><p className="text-xs text-slate-500 mb-1">Bio</p><p className="text-sm text-slate-700 leading-relaxed">{mentorDetailDialog.profile.bio}</p></div>
              )}

              {mentorDetailDialog.profile?.qualifications?.length > 0 && (
                <div><p className="text-xs text-slate-500 mb-1">Qualifications</p>
                  <div className="flex flex-wrap gap-1">{mentorDetailDialog.profile.qualifications.map((q, i) => <Badge key={i} variant="outline" className="text-xs">{q}</Badge>)}</div>
                </div>
              )}

              {mentorDetailDialog.profile?.publications?.length > 0 && (
                <div><p className="text-xs text-slate-500 mb-1">Publications</p>
                  <ul className="space-y-1">{mentorDetailDialog.profile.publications.map((p, i) => <li key={i} className="text-sm text-slate-700 flex items-start gap-1"><span className="text-[#1a5c5c] mt-0.5">-</span>{p}</li>)}</ul>
                </div>
              )}

              {mentorDetailDialog.profile?.research_areas?.length > 0 && (
                <div><p className="text-xs text-slate-500 mb-1">Research Areas</p>
                  <div className="flex flex-wrap gap-1">{mentorDetailDialog.profile.research_areas.map((r, i) => <Badge key={i} className="bg-[#1a5c5c]/10 text-[#1a5c5c] border-0 text-xs">{r}</Badge>)}</div>
                </div>
              )}

              {mentorDetailDialog.profile?.awards?.length > 0 && (
                <div><p className="text-xs text-slate-500 mb-1">Awards & Honors</p>
                  <ul className="space-y-1">{mentorDetailDialog.profile.awards.map((a, i) => <li key={i} className="text-sm text-slate-700 flex items-start gap-1"><span className="text-[#d4af37] mt-0.5">-</span>{a}</li>)}</ul>
                </div>
              )}

              <p className="text-xs text-slate-400">Joined: {new Date(mentorDetailDialog.created_at).toLocaleDateString()}</p>
            </div>
          )}
          <DialogFooter>
            {mentorDetailDialog && !mentorDetailDialog.verified && (
              <Button onClick={() => { verifyMentor(mentorDetailDialog.user_id, true); setMentorDetailDialog(null); }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="verify-from-detail-btn">
                <CheckCircle className="h-4 w-4 mr-2" /> Verify Mentor
              </Button>
            )}
            <Button variant="outline" onClick={() => setMentorDetailDialog(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
